import React from 'react';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import './DueDateBadge.scss';
import { IconNavCalendar, IconCancel } from 'components/Icons';
import { Tooltip } from '../Tooltip';
import type { FloatingAlignment, FloatingPosition } from '../hooks/useFloatingPosition';

export type DueDateBadgeSize = 'normal' | 'big';
export type DueDateBadgeStatus = 'pending' | 'past-due';

export interface DueDateBadgeProps {
  /** The due date */
  date: Date;
  /** Size variant */
  size?: DueDateBadgeSize;
  /** Status variant */
  status?: DueDateBadgeStatus;
  tooltipPosition?: FloatingPosition;
  tooltipAlignment?: FloatingAlignment;
  /** Show close button */
  showClose?: boolean;
  /** Callback when close button is clicked */
  onClose?: () => void;
  /** Callback when badge is clicked */
  onClick?: () => void;
  /** Additional className */
  className?: string;
}

/**
 * Displays a due date badge with relative time information
 */
export const DueDateBadge: React.FC<DueDateBadgeProps> = ({
  date,
  size = 'normal',
  status = 'pending',
  tooltipPosition = 'top',
  tooltipAlignment = 'start',
  showClose = false,
  onClose,
  onClick,
  className,
}) => {
  const prefix = prefixClass('ksq2-duedate-badge');

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  // Calculate days until/past due
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(date);
  dueDate.setHours(0, 0, 0, 0);
  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Format date as M/D
  const formattedDate = `${date.getMonth() + 1}/${date.getDate()}`;
  const maxDueDays = 90;

  const tooltipDate = dueDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const tooltipRelative = (() => {
    if (diffDays === 0) return 'Due today';
    if (diffDays > 0) return `${diffDays} day${diffDays === 1 ? '' : 's'} remaining`;
    const abs = Math.abs(diffDays);
    if (abs > maxDueDays) return `${maxDueDays}+ days past due`;
    return `${abs} day${abs === 1 ? '' : 's'} past due`;
  })();

  const tooltipContent = (
    <div>
      <div>
        <b>{tooltipDate}</b>
      </div>
      <div>{tooltipRelative}</div>
    </div>
  );

  // Format relative time
  let relativeTime: React.ReactNode = '';
  if (diffDays === 0) {
    relativeTime = 'Today';
  } else if (diffDays === 1) {
    relativeTime = <span>Tomorrow</span>;
  } else if (diffDays > 1 && diffDays < 7) {
    relativeTime = (
      <>
        in <span>{diffDays}</span> days
      </>
    );
  } else if (diffDays >= 7 && diffDays < 14) {
    relativeTime = (
      <>
        in <span>1</span> week
      </>
    );
  } else if (diffDays >= 14 && diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    relativeTime = (
      <>
        in <span>{weeks}</span> weeks
      </>
    );
  } else if (diffDays >= 30) {
    const months = Math.floor(diffDays / 30);
    relativeTime = (
      <>
        in <span>{months}</span> {months === 1 ? 'month' : 'months'}
      </>
    );
  } else if (diffDays === -1) {
    relativeTime = <span>Yesterday</span>;
  } else if (diffDays < -1 && diffDays > -maxDueDays) {
    relativeTime = (
      <>
        <span>{Math.abs(diffDays)} days</span> PAST DUE
      </>
    );
  } else if (diffDays <= -maxDueDays) {
    relativeTime = (
      <>
        <span>+{maxDueDays} days</span> PAST DUE
      </>
    );
  }

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose?.();
  };

  return (
    <Tooltip content={tooltipContent} position={tooltipPosition} alignment={tooltipAlignment}>
      <div className={classNames(prefix(), prefix(`--${size}`), prefix(`--${status}`), className)} onClick={onClick}>
        {/* Calendar icon */}
        <div className={prefix('__icon')}>
          <IconNavCalendar />
        </div>
        {/* Date and relative time */}
        <span className={prefix('__text')}>
          {diffDays >= 0 && <span className={prefix('__date')}>{formattedDate}</span>}
          {relativeTime !== '' && <span className={prefix('__relative')}>{relativeTime}</span>}
        </span>

        {/* Close button */}
        {showClose && (
          <button type="button" className={prefix('__close')} onClick={handleClose} aria-label="Clear due date">
            <IconCancel />
          </button>
        )}
      </div>
    </Tooltip>
  );
};

export default DueDateBadge;
